import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { formatBananas, formatSigned } from './format';
import { useLiveLeaderboard } from './live';
import { RollingNumber } from './rolling-number';

export function LeaderboardClient() {
  const data = useLiveLeaderboard();

  if (!data)
    return (
      <p className="font-mono text-primary/70 uppercase tracking-wider">
        Cargando…
      </p>
    );

  const myId = data.myRow?.userId;
  const prizeEnabled = data.prizeTopPercent > 0 && data.prizeCutoffRank > 0;
  const myQualifies =
    prizeEnabled &&
    data.myRow != null &&
    data.myRow.rank <= data.prizeCutoffRank;

  return (
    <div className="flex flex-col gap-6">
      <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <div className="border-primary/20 border-b pb-3">
          <p className="font-mono text-primary/70 text-xs uppercase tracking-wider">
            Ranking
          </p>
          <h1 className="font-bold text-3xl text-primary sm:text-4xl">
            Leaderboard
          </h1>
        </div>

        {data.resolved && (
          <div className="mt-4 border-primary border-l-4 pl-4">
            <p className="font-bold text-primary">Mercado resuelto</p>
            <p className="font-mono text-primary/70 text-xs">
              Las acciones ganadoras pagan 1 🍌 cada una.
            </p>
          </div>
        )}

        {prizeEnabled && (
          <div className="mt-4 border-primary border-l-4 pl-4">
            <p className="font-bold text-primary">
              🏆 Top {data.prizeTopPercent}% gana premio especial
            </p>
            <p className="font-mono text-primary/70 text-xs">
              Corte: posición #{data.prizeCutoffRank} de {data.rows.length}
              {myQualifies ? ' — ¡clasificas!' : ''}
            </p>
          </div>
        )}

        {data.myRow && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="border-primary border-l-4 pl-4">
              <p className="font-mono text-primary/70 text-xs uppercase tracking-wider">
                Tu posición
              </p>
              <p className="font-bold font-mono text-3xl text-primary">
                #{data.myRow.rank}
              </p>
            </div>
            <div className="border-primary border-l-4 pl-4">
              <p className="font-mono text-primary/70 text-xs uppercase tracking-wider">
                Total
              </p>
              <p className="font-bold font-mono text-2xl text-primary">
                <RollingNumber
                  value={data.myRow.totalBananas}
                  format={formatBananas}
                />
              </p>
            </div>
            <div className="border-primary border-l-4 pl-4">
              <p className="font-mono text-primary/70 text-xs uppercase tracking-wider">
                P&L
              </p>
              <p
                className={cn(
                  'font-bold font-mono text-2xl',
                  data.myRow.pnlBananas >= 0
                    ? 'text-green-600'
                    : 'text-red-600',
                )}
              >
                <RollingNumber
                  value={data.myRow.pnlBananas}
                  format={(n) => formatSigned(n, ' 🍌')}
                />
              </p>
            </div>
          </div>
        )}
      </section>

      <section className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8">
        <h2 className="mb-4 border-primary/20 border-b pb-3 font-bold text-primary text-xl">
          Todos los traders
        </h2>
        <div className="flex flex-col gap-1">
          {data.rows.map((r) => {
            const qualifies = prizeEnabled && r.rank <= data.prizeCutoffRank;
            const isCutoff = prizeEnabled && r.rank === data.prizeCutoffRank;
            const isMe = r.userId === myId;
            const positive = r.pnlBananas >= 0;
            return (
              <div
                key={r.userId}
                className={cn(
                  'grid grid-cols-12 items-center gap-3 border-l-4 py-2 pr-3 pl-4 transition-colors',
                  isMe
                    ? 'border-primary bg-primary/10'
                    : 'border-primary/40 hover:bg-primary/5',
                  isCutoff && 'border-b-2 border-b-primary',
                )}
              >
                <span className="col-span-2 font-bold font-mono text-primary tabular-nums sm:col-span-1">
                  #{r.rank}
                </span>
                <div className="col-span-6 flex items-center gap-2 sm:col-span-6">
                  <Avatar className="size-7 border-2 border-primary">
                    {r.userImage && <AvatarImage src={r.userImage} />}
                    <AvatarFallback className="bg-background font-mono text-primary text-xs">
                      {(r.userName ?? '?').slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate font-bold text-primary">
                    {r.userName ?? 'anon'}
                  </span>
                  {qualifies && (
                    <span
                      title={`Top ${data.prizeTopPercent}% — gana premio especial`}
                      className="text-sm"
                    >
                      🏆
                    </span>
                  )}
                  {isMe && (
                    <span className="bg-primary px-1.5 py-0.5 font-mono text-[9px] text-background uppercase">
                      Tú
                    </span>
                  )}
                </div>
                <span className="col-span-2 text-right font-bold font-mono text-primary text-sm tabular-nums sm:col-span-3">
                  <RollingNumber
                    value={r.totalBananas}
                    format={formatBananas}
                  />
                </span>
                <span
                  className={cn(
                    'col-span-2 text-right font-bold font-mono text-sm tabular-nums',
                    positive ? 'text-green-600' : 'text-red-600',
                  )}
                >
                  {positive ? '+' : ''}
                  {r.pnlBananas}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
